// @ts-nocheck — JSX-only file; props typed via JSDoc and evolve frequently
import React, { useEffect, useMemo, useRef, useState } from "react"
import Alert from "react-bootstrap/Alert"
import { Check, ClipboardList, Pencil } from "lucide-react"
import { formatContactMethodLabel } from "../utils/contactMethodLabel.js"
import Accordion from "./Accordion.jsx"
import { isReviewSectionComplete } from "./sectionCompletion.js"
import { OrderFormCheckbox } from "./EnterKeyControls.jsx"
import {
    groupErrorsBySection,
    sectionTitleForErrorSection,
} from "./orderFormValidation.js"
import signatureImg from "../assets/signature.png"

function SectionTitle({ children }) {
    return <h3 className="h5 fw-semibold mb-3">{children}</h3>
}

function InfoRow({ label, value }) {
    if (!value) {
        return null
    }
    return (
        <div className="d-flex justify-content-between py-2 border-bottom">
            <span className="text-muted">{label}:</span>
            <span className="text-dark fw-semibold">{value}</span>
        </div>
    )
}

function Card({ title, onEdit, headerRight = null, children }) {
    return (
        <div className="bg-white rounded p-4 shadow-sm border">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <SectionTitle>{title}</SectionTitle>
                {headerRight ? (
                    headerRight
                ) : (
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm d-flex align-items-center"
                        onClick={onEdit}
                    >
                        <Pencil size={14} className="me-1" /> Edit
                    </button>
                )}
            </div>
            {children}
        </div>
    )
}

function ReadonlyCheckbox({ checked }) {
    return (
        <input
            type="checkbox"
            className="form-check-input"
            checked={Boolean(checked)}
            disabled
            readOnly
            tabIndex={-1}
            style={{ pointerEvents: "none" }}
        />
    )
}

function LicenseImagesRow({ frontChecked, backChecked }) {
    return (
        <div className="d-flex justify-content-between py-2 border-bottom">
            <span className="text-muted">License Images Included:</span>
            <span className="text-dark fw-semibold">
                <span className="d-inline-flex align-items-center gap-2">
                    <span className="d-inline-flex align-items-center gap-2">
                        Front <ReadonlyCheckbox checked={frontChecked} />
                    </span>
                    <span className="d-inline-flex align-items-center gap-2 ps-2">
                        Back <ReadonlyCheckbox checked={backChecked} />
                    </span>
                </span>
            </span>
        </div>
    )
}

function formatProcessingInstructionSummary(instruction, lookup) {
    const speciesId = instruction?.speciesId == null ? null : Number(instruction.speciesId)
    const processId = instruction?.processId == null ? null : Number(instruction.processId)
    const quantity = Number(instruction?.quantity ?? 0)
    const unit = String(instruction?.unit ?? "").trim()

    const species = speciesId == null ? "" : (lookup?.speciesNameById?.get(speciesId) ?? "")
    const method = processId == null ? "" : (lookup?.processNameById?.get(processId) ?? "")

    const parts = []
    if (species) parts.push(species)
    if (method) parts.push(method)
    if (quantity > 0 && unit) parts.push(`${quantity} ${unit}`)
    else if (quantity > 0) parts.push(String(quantity))

    return parts.join(" – ")
}

function optionProcessIds(opt) {
    return [
        opt?.process_id1,
        opt?.process_id2,
        opt?.process_id3,
        opt?.process_id4,
        opt?.process_id5,
    ]
        .map((x) => (x == null ? null : Number(x)))
        .filter((x) => Number.isFinite(x))
}

function selectedOptionShortNameListForProcessId(processId, optionsDataset, selectedById) {
    const pid = processId == null ? null : Number(processId)
    if (!Number.isFinite(pid)) return []

    const selected = selectedById ?? {}
    const out = []
    for (const opt of optionsDataset ?? []) {
        const oid = opt?.id
        const optionId = oid == null ? null : Number(oid)
        if (!Number.isFinite(optionId)) continue
        if (!selected[optionId]) continue
        if (!optionProcessIds(opt).includes(pid)) continue
        const sn = String(
            opt?.short_name ?? opt?.shortName ?? opt?.ShortName ?? ""
        ).trim()
        if (sn) out.push(sn)
    }
    return out
}

/**
 * @param {{
 *   model: any,
 *   onJumpTo: (index: number) => void,
 *   onJumpToDropoff?: () => void,
 *   onSubmit?: () => void | Promise<void>,
 *   isSubmitting?: boolean,
 *   termsAccepted?: boolean,
 *   onTermsAcceptedChange?: (v: boolean) => void,
 *   signatureBlob?: Blob | null,
 *   onSignatureBlobChange?: (b: Blob | null) => void,
 *   onSignatureClearedByUser?: () => void,
 *   newsletterOptIn?: boolean,
 *   onNewsletterOptInChange?: (v: boolean) => void,
 *   submitLabel?: string,
 *   title?: string,
 *   isOpen?: boolean,
 *   touched?: boolean,
 *   statusLabel?: string | null,
 *   disabled?: boolean,
 *   onHeaderClick?: () => void,
 *   enablePrepaidAmount?: boolean,
 *   validationErrors?: Array<{ section: number | "dropoff", field: string, message: string, domId: string, label: string }>,
 *   hasErrors?: boolean,
 *   fieldErrors?: Record<string, string> | null,
 *   showFieldErrors?: boolean,
 *   onReviewVisible?: () => void,
 *   onJumpToError?: (error: { section: number | "dropoff", domId: string }) => void,
 * }} props
 */
export default function ReviewSection({
    model,
    speciesOptions = [],
    processOptions = [],
    optionsDataset = [],
    locations = [],
    onJumpTo,
    onJumpToDropoff,
    onSubmit,
    isSubmitting = false,
    termsAccepted: termsAcceptedProp,
    onTermsAcceptedChange,
    signatureBlob,
    onSignatureBlobChange,
    onSignatureClearedByUser,
    newsletterOptIn: newsletterOptInProp,
    onNewsletterOptInChange,
    submitLabel = "Submit Order",
    title = "6. Review & Submit",
    isOpen,
    touched = false,
    statusLabel = null,
    disabled = false,
    enablePrepaidAmount = true,
    onHeaderClick,
    validationErrors = [],
    hasErrors = false,
    fieldErrors = null,
    showFieldErrors = false,
    onReviewVisible,
    onJumpToError,
}) {
    const SIGNATURE_BOX_W = 360
    const SIGNATURE_BOX_H = 110

    const [termsAcceptedLocal, setTermsAcceptedLocal] = useState(false)
    const termsAccepted = termsAcceptedProp ?? termsAcceptedLocal
    const [orderUpdatesOptInLocal, setOrderUpdatesOptInLocal] = useState(true)
    const [newsletterOptInLocal, setNewsletterOptInLocal] = useState(false)
    const newsletterOptIn = newsletterOptInProp ?? newsletterOptInLocal
    const isCompleted = isReviewSectionComplete(model) && Boolean(touched)
    const reviewRootRef = useRef(/** @type {HTMLDivElement | null} */(null))
    const didFireReviewVisibleRef = useRef(false)

    const errorsBySection = useMemo(
        () => groupErrorsBySection(validationErrors ?? []),
        [validationErrors]
    )

    useEffect(() => {
        if (!onReviewVisible || didFireReviewVisibleRef.current || !isOpen) {
            return
        }
        const el = reviewRootRef.current
        if (!el || typeof IntersectionObserver === "undefined") {
            return
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (didFireReviewVisibleRef.current) {
                    return
                }
                if (entries.some((e) => e.isIntersecting && e.intersectionRatio > 0)) {
                    didFireReviewVisibleRef.current = true
                    onReviewVisible()
                    observer.disconnect()
                }
            },
            { threshold: 0.15 }
        )
        observer.observe(el)
        return () => observer.disconnect()
    }, [onReviewVisible, isOpen])

    const [showSignatureCanvas, setShowSignatureCanvas] = useState(false)
    const [isDrawing, setIsDrawing] = useState(false)
    const [signatureHasInk, setSignatureHasInk] = useState(false)
    const canvasRef = useRef(/** @type {HTMLCanvasElement | null} */(null))
    const didHydrateSignatureRef = useRef(false)
    const pendingStrokeStartRef = useRef(
        /** @type {{ pointerId: number, clientX: number, clientY: number } | null} */(null)
    )

    const signatureIsProvided = Boolean(signatureBlob instanceof Blob) || signatureHasInk

    const lookup = useMemo(() => {
        const speciesNameById = new Map()
        const processNameById = new Map()
        for (const s of speciesOptions ?? []) {
            const id = s?.id
            if (id != null) speciesNameById.set(Number(id), String(s?.name ?? ""))
        }
        for (const p of processOptions ?? []) {
            const id = p?.id
            if (id != null) processNameById.set(Number(id), String(p?.name ?? ""))
        }
        return { speciesNameById, processNameById }
    }, [speciesOptions, processOptions])

    function ensureCanvasBackingStoreSize() {
        const canvas = canvasRef.current
        if (!canvas) return
        const dpr = Math.max(1, window.devicePixelRatio || 1)
        const nextW = Math.round(SIGNATURE_BOX_W * dpr)
        const nextH = Math.round(SIGNATURE_BOX_H * dpr)
        if (canvas.width === nextW && canvas.height === nextH) {
            return
        }

        // We keep a fixed logical size; avoid resampling signatures across layout changes.
        // If DPR changes (rare), we clear rather than blur/resample.
        // Users can re-sign; this is preferable to progressive blur.
        if (signatureHasInk) {
            setSignatureHasInk(false)
            onSignatureBlobChange?.(null)
        }

        canvas.width = nextW
        canvas.height = nextH

        const ctx = canvas.getContext("2d")
        if (!ctx) return
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
        ctx.lineCap = "round"
        ctx.lineJoin = "round"
        ctx.strokeStyle = "#0f172a"
        ctx.lineWidth = 2.2
    }

    useEffect(() => {
        if (!showSignatureCanvas) return
        ensureCanvasBackingStoreSize()
        // No responsive scaling; keep fixed 360×110 always.
    }, [showSignatureCanvas])

    useEffect(() => {
        if (!(signatureBlob instanceof Blob)) {
            didHydrateSignatureRef.current = false
            return
        }
        if (didHydrateSignatureRef.current) {
            return
        }
        // Show the canvas so the preloaded signature is visible immediately (v1 edit behavior).
        setShowSignatureCanvas(true)
    }, [signatureBlob])

    useEffect(() => {
        if (!(signatureBlob instanceof Blob)) return
        if (!showSignatureCanvas) return
        if (didHydrateSignatureRef.current) return
        const canvas = canvasRef.current
        const ctx = canvas?.getContext("2d")
        if (!canvas || !ctx) return

        didHydrateSignatureRef.current = true
        ensureCanvasBackingStoreSize()

        const url = URL.createObjectURL(signatureBlob)
        const img = new Image()
        img.onload = () => {
            try {
                ctx.clearRect(0, 0, SIGNATURE_BOX_W, SIGNATURE_BOX_H)
                ctx.drawImage(img, 0, 0, SIGNATURE_BOX_W, SIGNATURE_BOX_H)
                setSignatureHasInk(true)
            } finally {
                URL.revokeObjectURL(url)
            }
        }
        img.onerror = () => {
            URL.revokeObjectURL(url)
        }
        img.src = url
    }, [signatureBlob, showSignatureCanvas])

    useEffect(() => {
        if (!showSignatureCanvas) return
        const pending = pendingStrokeStartRef.current
        if (!pending) return

        // Start drawing immediately after the placeholder click that opened the canvas (v1 behavior).
        window.requestAnimationFrame(() => {
            ensureCanvasBackingStoreSize()
            const canvas = canvasRef.current
            const ctx = canvas?.getContext("2d")
            if (!canvas || !ctx) return

            try {
                canvas.setPointerCapture?.(pending.pointerId)
            } catch (_) {
                // Ignore capture failures (older browsers / edge cases).
            }
            setIsDrawing(true)
            const p = getCanvasPoint({ clientX: pending.clientX, clientY: pending.clientY })
            ctx.beginPath()
            ctx.moveTo(p.x, p.y)
            pendingStrokeStartRef.current = null
        })
    }, [showSignatureCanvas])

    function getCanvasPoint(e) {
        const canvas = canvasRef.current
        if (!canvas) return { x: 0, y: 0 }
        const rect = canvas.getBoundingClientRect()
        const clientX = e.clientX ?? 0
        const clientY = e.clientY ?? 0
        const sx = rect.width > 0 ? SIGNATURE_BOX_W / rect.width : 1
        const sy = rect.height > 0 ? SIGNATURE_BOX_H / rect.height : 1
        return {
            x: (clientX - rect.left) * sx,
            y: (clientY - rect.top) * sy,
        }
    }

    async function emitSignatureBlob() {
        const canvas = canvasRef.current
        if (!canvas) return
        if (!signatureHasInk) {
            onSignatureBlobChange?.(null)
            return
        }
        canvas.toBlob(
            (blob) => {
                if (blob) onSignatureBlobChange?.(blob)
            },
            "image/png",
            0.92
        )
    }

    async function handleSubmit() {
        await onSubmit?.()
    }

    function jumpToError(err) {
        if (onJumpToError) {
            onJumpToError(err)
            return
        }
        if (err.section === "dropoff") {
            onJumpToDropoff?.()
        } else if (typeof err.section === "number") {
            onJumpTo?.(err.section)
        }
        if (err.domId) {
            requestAnimationFrame(() => {
                document.getElementById(err.domId)?.scrollIntoView?.({ behavior: "smooth", block: "center" })
                document.getElementById(err.domId)?.focus?.({ preventScroll: true })
            })
        }
    }

    return (
        <div ref={reviewRootRef}>
        <Accordion
            title={title}
            isActive={Boolean(isOpen)}
            isCompleted={isCompleted}
            statusLabel={statusLabel}
            hasErrors={hasErrors}
            disabled={disabled}
            onClick={onHeaderClick}
        >
            <div className="row g-4">
                {validationErrors?.length > 0 ? (
                    <div className="col-12">
                        <div className="alert alert-danger mb-0" role="alert">
                            <div className="fw-semibold mb-2">Please fix the following before submitting:</div>
                            <ul className="mb-0 ps-3">
                                {[...errorsBySection.entries()].map(([sectionKey, errs]) => (
                                    <li key={String(sectionKey)} className="mb-2">
                                        <div className="fw-semibold">{sectionTitleForErrorSection(sectionKey)}</div>
                                        <ul className="mb-0 ps-3">
                                            {errs.map((err) => (
                                                <li key={`${err.field}-${err.domId}`}>
                                                    <button
                                                        type="button"
                                                        className="btn btn-link btn-sm p-0 align-baseline text-danger text-decoration-underline"
                                                        onClick={() => jumpToError(err)}
                                                    >
                                                        {err.label}: {err.message}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                ) : null}
                <div className="col-12">
                    <div
                        className="p-3 rounded d-flex align-items-start"
                        style={{ backgroundColor: "#e7f3ff", gap: "12px" }}
                    >
                        <ClipboardList className="text-primary mt-1" size={20} />
                        <div>
                            <h3
                                className="fw-semibold"
                                style={{ color: "#1e3a8a", fontSize: "1rem", marginBottom: "4px" }}
                            >
                                Order Review
                            </h3>
                            <p className="mb-0" style={{ color: "#1e40af", fontSize: "0.875rem" }}>
                                Please review your order details before submitting.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="col-12">
                    <Card
                        title="Drop-off"
                        onEdit={() => (onJumpToDropoff ? onJumpToDropoff() : onJumpTo(0))}
                    >
                        <InfoRow
                            label="Location"
                            value={
                                locations.find(
                                    (l) =>
                                        model.dropoff.locationId != null &&
                                        Number(l?.id) === Number(model.dropoff.locationId)
                                )?.name ?? ""
                            }
                        />
                        <InfoRow label="Date" value={model.dropoff.date} />
                    </Card>
                </div>

                <div className="col-12">
                    <Card title="Contact Information" onEdit={() => onJumpTo(0)}>
                        <InfoRow label="Name" value={`${model.contact.firstName} ${model.contact.lastName}`} />
                        <InfoRow label="Company" value={model.contact.companyName} />
                        <InfoRow label="Phone" value={model.contact.phone} />
                        <InfoRow
                            label="Preferred Contact Method"
                            value={formatContactMethodLabel(model.contact.contact_method)}
                        />
                        <InfoRow
                            label="Staff member"
                            value={model.contact.is_staff ? "Yes" : "No"}
                        />
                        {enablePrepaidAmount ? (
                            <InfoRow
                                label="Prepaid Amount"
                                value={(() => {
                                    const raw = String(model.contact.prepaidAmount ?? "").trim()
                                    if (!raw) return "—"
                                    const n = Number(raw)
                                    return Number.isFinite(n) ? `$${n.toFixed(2)}` : `$${raw}`
                                })()}
                            />
                        ) : null}
                        <InfoRow label="Trip Code" value={model.contact.tripCode} />
                        <InfoRow label="Fish Tag" value={model.contact.fishTag} />
                        <InfoRow label="License Number" value={model.contact.licenseNumber} />
                        <LicenseImagesRow
                            frontChecked={
                                Boolean(model.contact.licenseFileFront) ||
                                Boolean(model.contact.licenseFrontImg)
                            }
                            backChecked={
                                Boolean(model.contact.licenseFileBack) ||
                                Boolean(model.contact.licenseBackImg)
                            }
                        />
                        {model.contact.customerId == null ? (
                            <Alert variant="info" className="mt-3 mb-0">
                                A new customer with this information will be created and attached to the order.
                            </Alert>
                        ) : model.contact.updateCustomerFields ? (
                            <Alert variant="warning" className="mt-3 mb-0">
                                The existing customer will be updated to reflect the contact changes above.
                            </Alert>
                        ) : null}
                    </Card>
                </div>

                <div className="col-12">
                    <Card title="Delivery Information" onEdit={() => onJumpTo(1)}>
                        <InfoRow
                            label="Method"
                            value={model.delivery.method === "ship" ? "Ship to Address" : "Pickup"}
                        />
                        {model.delivery.method === "pickup" && (
                            <>
                                <InfoRow
                                    label="Pickup Location"
                                    value={
                                        locations.find(
                                            (l) =>
                                                model.delivery.pickupLocationId != null &&
                                                Number(l?.id) ===
                                                Number(model.delivery.pickupLocationId)
                                        )?.name ?? ""
                                    }
                                />
                                <InfoRow
                                    label="Hold for pick up"
                                    value={model.delivery.holdForPickup ? "Yes" : "No"}
                                />
                                {model.delivery.holdForPickup && (
                                    <InfoRow
                                        label="Number of Containers/Boxes"
                                        value={model.processing.containers}
                                    />
                                )}
                            </>
                        )}
                    </Card>
                </div>

                <div className="col-12">
                    <Card title="Shipping Information" onEdit={() => onJumpTo(2)}>
                        <InfoRow
                            label="Ship To"
                            value={`${model.shipping.firstName} ${model.shipping.lastName}`}
                        />
                        <InfoRow label="Address" value={model.shipping.streetAddress} />
                        <InfoRow
                            label="City / Province"
                            value={`${model.shipping.city}, ${model.shipping.province}`}
                        />
                        <InfoRow label="Country" value={model.shipping.country} />
                        {model.delivery.method === "ship" && model.shipping.attachAddressToCustomer ? (
                            <Alert variant="warning" className="mt-3 mb-0">
                                {model.shipping.shippingCustomerId != null ?
                                    "This will replace the customer's default shipping address for future orders." :
                                    "This will be used as the customer's default shipping address for future orders."}
                            </Alert>
                        ) : null}
                    </Card>
                </div>

                <div className="col-12">
                    <Card title="Billing Information" onEdit={() => onJumpTo(3)}>
                        <InfoRow
                            label="Bill To"
                            value={`${model.billing.firstName} ${model.billing.lastName}`}
                        />
                        <InfoRow label="Address" value={model.billing.streetAddress} />
                        <InfoRow
                            label="City / Province"
                            value={`${model.billing.city}, ${model.billing.province}`}
                        />
                        <InfoRow label="Country" value={model.billing.country} />
                        {(() => {
                            if (model.billing.useShippingAddress) {
                                return null
                            }
                            if (!model.billing.attachAddressToCustomer) {
                                return null
                            }
                            return (
                                <Alert variant="warning" className="mt-3 mb-0">
                                    {model.billing.billingCustomerId != null ?
                                        "This will replace the customer's default billing address for future orders." :
                                        "This will be used as the customer's default billing address for future orders."}
                                </Alert>
                            )
                        })()}
                    </Card>
                </div>

                <div className="col-12">
                    <Card
                        title="Processing Details"
                        onEdit={() => onJumpTo(4)}
                        headerRight={
                            model.delivery.holdForPickup ? (
                                <span className="text-secondary fw-semibold small">
                                    Not applicable
                                </span>
                            ) : null
                        }
                    >
                        {model.delivery.holdForPickup ? null : (
                            <>
                                <InfoRow
                                    label="Total Weight"
                                    value={(() => {
                                        const w = String(model.processing.totalWeight ?? "").trim()
                                        return w ? `${w} lbs` : "—"
                                    })()}
                                />
                                <InfoRow
                                    label="Number of Containers/Boxes"
                                    value={(() => {
                                        const c = String(model.processing.containers ?? "").trim()
                                        return c ? c : "—"
                                    })()}
                                />
                                {(model.processing.instructions ?? []).map((instruction, i) => (
                                    <InfoRow
                                        key={i}
                                        label={`Process #${i + 1}`}
                                        value={(() => {
                                            const base =
                                                formatProcessingInstructionSummary(
                                                    instruction,
                                                    lookup
                                                )
                                            const pid =
                                                instruction?.processId == null ?
                                                    null :
                                                    Number(instruction.processId)
                                            const method =
                                                lookup.processNameById.get(
                                                    Number(instruction?.processId)
                                                ) ?? ""
                                            const sns =
                                                selectedOptionShortNameListForProcessId(
                                                    pid,
                                                    optionsDataset,
                                                    model.processing.options
                                                )
                                            if (!method || sns.length === 0) {
                                                return base
                                            }
                                            // Insert " (SN1/SN2)" immediately after the method token.
                                            const suffix = ` (${sns.join("/")})`
                                            const needle = ` – ${method}`
                                            if (base.includes(needle)) {
                                                return base.replace(needle, `${needle}${suffix}`)
                                            }
                                            return `${base}${suffix}`
                                        })()}
                                    />
                                ))}
                            </>
                        )}
                    </Card>
                </div>

                <div className="col-12">
                    <div className="bg-white rounded p-4 shadow-sm border">
                        <h4 className="h5 fw-semibold mb-3">Terms and Conditions</h4>
                        <p className="text-muted mb-2">
                            By submitting this order, you agree to our processing terms and
                            conditions. This includes acknowledging that:
                        </p>
                        <ul className="text-muted" style={{ paddingLeft: "1.25rem" }}>
                            <li>All weights are approximate and may vary</li>
                            <li>Processing times depend on seasonal volume</li>
                            <li>
                                You are responsible for ensuring your fish is properly stored
                                and delivered in good condition
                            </li>
                            <li>
                                Payment is required at pickup/delivery of processed products
                            </li>
                        </ul>

                        <div className="form-check mt-3" id="review-terms">
                            <OrderFormCheckbox
                                checked={termsAccepted}
                                onCheckedChange={(checked) => {
                                    const v = Boolean(checked)
                                    onTermsAcceptedChange?.(v)
                                    setTermsAcceptedLocal(v)
                                }}
                                className={`form-check-input${showFieldErrors && fieldErrors?.termsAccepted ? " is-invalid" : ""}`}
                                id="termsAccepted"
                            />
                            <label htmlFor="termsAccepted" className="form-check-label">
                                I have read and agree to the terms and conditions
                            </label>
                            {showFieldErrors && fieldErrors?.termsAccepted ? (
                                <div className="invalid-feedback d-block">{fieldErrors.termsAccepted}</div>
                            ) : null}
                        </div>

                        <div className="mt-4" id="review-signature">
                            <div className="fw-semibold mb-2">Signature</div>

                            <div className="d-flex flex-column flex-xl-row gap-3 align-items-start">
                                <div style={{ width: `${SIGNATURE_BOX_W}px`, flex: "0 0 auto" }}>
                                    <div
                                        className={`rounded border ${
                                            signatureIsProvided
                                                ? ""
                                                : showFieldErrors && fieldErrors?.signature
                                                  ? "border-2 border-danger"
                                                  : "border-2"
                                        }`}
                                        style={{
                                            width: `${SIGNATURE_BOX_W}px`,
                                            height: `${SIGNATURE_BOX_H}px`,
                                            borderColor:
                                                showFieldErrors && fieldErrors?.signature && !signatureIsProvided
                                                    ? undefined
                                                    : "#cbd5e1",
                                            backgroundColor: showSignatureCanvas ? "#ffffff" : "#f8fafc",
                                            position: "relative",
                                            overflow: "hidden",
                                        }}
                                        onPointerDown={(e) => {
                                            if (showSignatureCanvas) return
                                            // Save the down event so we can begin the stroke after the canvas is visible.
                                            pendingStrokeStartRef.current = {
                                                pointerId: e.pointerId,
                                                clientX: e.clientX,
                                                clientY: e.clientY,
                                            }
                                            setShowSignatureCanvas(true)
                                        }}
                                        role={!showSignatureCanvas ? "button" : undefined}
                                        tabIndex={!showSignatureCanvas ? 0 : -1}
                                        aria-label="Signature box"
                                    >
                                        {!showSignatureCanvas ? (
                                            <div className="h-100 w-100">
                                                <img
                                                    src={signatureImg}
                                                    alt="Signature placeholder"
                                                    style={{
                                                        display: "block",
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "cover",
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            <canvas
                                                ref={canvasRef}
                                                style={{
                                                    width: `${SIGNATURE_BOX_W}px`,
                                                    height: `${SIGNATURE_BOX_H}px`,
                                                    touchAction: "none",
                                                    display: "block",
                                                }}
                                                onPointerDown={(e) => {
                                                    ensureCanvasBackingStoreSize()
                                                    setIsDrawing(true)
                                                    const canvas = canvasRef.current
                                                    const ctx = canvas?.getContext("2d")
                                                    if (!canvas || !ctx) return
                                                    canvas.setPointerCapture?.(e.pointerId)
                                                    const p = getCanvasPoint(e)
                                                    ctx.beginPath()
                                                    ctx.moveTo(p.x, p.y)
                                                }}
                                                onPointerMove={(e) => {
                                                    if (!isDrawing) return
                                                    const canvas = canvasRef.current
                                                    const ctx = canvas?.getContext("2d")
                                                    if (!canvas || !ctx) return
                                                    const p = getCanvasPoint(e)
                                                    ctx.lineTo(p.x, p.y)
                                                    ctx.stroke()
                                                    if (!signatureHasInk) setSignatureHasInk(true)
                                                }}
                                                onPointerUp={(e) => {
                                                    setIsDrawing(false)
                                                    const canvas = canvasRef.current
                                                    canvas?.releasePointerCapture?.(e.pointerId)
                                                    emitSignatureBlob()
                                                }}
                                                onPointerCancel={(e) => {
                                                    setIsDrawing(false)
                                                    const canvas = canvasRef.current
                                                    canvas?.releasePointerCapture?.(e.pointerId)
                                                    emitSignatureBlob()
                                                }}
                                            />
                                        )}
                                    </div>

                                    <div className="d-flex justify-content-end" style={{ maxWidth: "360px" }}>
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm mt-2"
                                            onClick={() => {
                                                const canvas = canvasRef.current
                                                if (canvas) {
                                                    const ctx = canvas.getContext("2d")
                                                    ctx?.clearRect(0, 0, canvas.width, canvas.height)
                                                }
                                                setSignatureHasInk(false)
                                                onSignatureBlobChange?.(null)
                                                onSignatureClearedByUser?.()
                                                setShowSignatureCanvas(false)
                                            }}
                                            disabled={
                                                !showSignatureCanvas &&
                                                !signatureHasInk &&
                                                !(signatureBlob instanceof Blob)
                                            }
                                        >
                                            Clear
                                        </button>
                                    </div>
                                    {showFieldErrors && fieldErrors?.signature && !signatureIsProvided ? (
                                        <div className="text-danger small mt-1">{fieldErrors.signature}</div>
                                    ) : null}
                                </div>

                                <div style={{ minWidth: 0, flex: "1 1 auto" }}>
                                    <p className="mb-0 text-muted" style={{ maxWidth: "42rem" }}>
                                        This signature represents acceptance of the processing choices on your form. Any
                                        change requests placed afterwards will be subject to a surcharge and are{" "}
                                        <span className="fw-semibold">not guaranteed.</span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-12">
                    <div className="bg-white rounded p-4 shadow-sm border">
                        <div>
                            <h4 className="h5 fw-semibold mb-3">Notifications</h4>
                            <p className="text-muted mb-3">
                                We send transactional updates about your order (e.g., receipt,
                                status updates, pickup/shipping notices). These are not
                                marketing messages.
                            </p>
                            <div className="form-check">
                                <OrderFormCheckbox
                                    checked={orderUpdatesOptInLocal}
                                    onCheckedChange={(checked) => {
                                        setOrderUpdatesOptInLocal(Boolean(checked))
                                    }}
                                    className="form-check-input"
                                    id="notificationsOptIn"
                                />
                                <label
                                    htmlFor="notificationsOptIn"
                                    className="form-check-label"
                                >
                                    Send me order updates by email and text messages
                                </label>
                            </div>
                            <div className="form-check mt-2">
                                <OrderFormCheckbox
                                    checked={newsletterOptIn}
                                    onCheckedChange={(checked) => {
                                        const v = Boolean(checked)
                                        onNewsletterOptInChange?.(v)
                                        setNewsletterOptInLocal(v)
                                    }}
                                    className="form-check-input"
                                    id="newsletterOptIn"
                                />
                                <label htmlFor="newsletterOptIn" className="form-check-label">
                                    Sign me up for the newsletter
                                </label>
                            </div>
                            <small className="text-secondary d-block mt-2">
                                Message and data rates may apply. You can stop SMS updates at
                                any time by replying STOP.
                            </small>
                        </div>
                    </div>
                </div>

                <div className="col-12">
                    <div className="d-flex justify-content-end pt-3">
                        <button
                            type="button"
                            disabled={isSubmitting}
                            className="btn btn-primary px-5 py-2 d-flex align-items-center"
                            style={{ gap: "8px" }}
                            onClick={handleSubmit}
                        >
                            {isSubmitting ? (
                                <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                />
                            ) : (
                                <Check size={20} />
                            )}
                            <span>{submitLabel}</span>
                        </button>
                    </div>
                </div>
            </div>
        </Accordion>
        </div>
    )
}
